import { Alert, Link as MuiLink, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetProjectConfig,
  useGetScheduleDaypartsWithOverrides,
} from 'src/hooks';
import { useGetSchedules } from 'src/hooks/api/schedules/useGetAllSchedules';
import { lang, useUserRole } from 'src/utils';

interface DaypartDisabledAlertProps {
  navigateTo: string;
  locationGroupId: number;
  locationCode?: string;
  selectedDaypartId?: number;
  onVisibilityChange?: (isVisible: boolean) => void;
}

const useDaypartDisabledAlertVisibility = (
  locationGroupId: number,
  locationCode?: string,
  selectedDaypartId?: number,
) => {
  const { isViewOnly, isOwnerOperator } = useUserRole();
  const { data: lgWithDayparts, isLoading } =
    useGetScheduleDaypartsWithOverrides(locationGroupId, locationCode);
  const { data: projectConfig, isLoading: isProjectConfigLoading } =
    useGetProjectConfig();
  const { data: schedulesData = [], isLoading: isSchedulesLoading } =
    useGetSchedules(
      [
        `location_group_id=${locationGroupId}`,
        `daypart_id=${selectedDaypartId}`,
        ...(locationCode ? [`location_code=${locationCode}`] : []),
      ],
      Boolean(selectedDaypartId) && Boolean(locationGroupId),
    );

  const hasSchedules = schedulesData.length > 0;
  const isDaypartEnabled = lgWithDayparts?.dayparts.find(
    (item) => item.id === selectedDaypartId,
  )?.enabled;

  return {
    isVisible:
      !isOwnerOperator &&
      !isViewOnly &&
      !isLoading &&
      !isProjectConfigLoading &&
      !isSchedulesLoading &&
      !projectConfig?.rbStoreIntegration &&
      !isDaypartEnabled &&
      hasSchedules,
  };
};

export const DaypartDisabledAlert = (props: DaypartDisabledAlertProps) => {
  const {
    navigateTo,
    locationGroupId,
    locationCode,
    selectedDaypartId,
    onVisibilityChange,
  } = props;

  const { isVisible } = useDaypartDisabledAlertVisibility(
    locationGroupId,
    locationCode,
    selectedDaypartId,
  );

  useEffect(() => {
    onVisibilityChange?.(isVisible);
  }, [isVisible, onVisibilityChange]);

  if (!isVisible) return null;

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{
        justifyContent: 'center',
        '& .MuiAlert-message': {
          display: 'flex',
          gap: 1,
        },
      }}>
      <Typography variant="body2">
        {lang.alertMessages.daypartSwitchedOff}
      </Typography>
      <MuiLink component={Link} to={navigateTo}>
        {lang.alertMessages.accessTimings}
      </MuiLink>
    </Alert>
  );
};
